import React, {ReactNode} from 'react';

import cs from 'classnames';
import range from 'lodash/range';
import styled from 'styled-components';

import {TooltipList} from 'app/components/general/TooltipList';
import {AttachTooltip} from 'app/general/overlays';
import {getBalancedGrid} from 'app/utils/math';

import {SingleStyledBoxDIV} from './BaseAvatar';
import {AvatarTooltipsProps, getTooltipContent} from './tooltips';

import type {OverlayPlacementType} from 'app/general/overlays';

const MAX_COLS = 3;
const MAX_USERS_SHOW = 9;

export interface SingleAvatarAdditionalProps {
  className?: string;
  tooltipTitle?: string | ReactNode;
  tooltipPlacement?: OverlayPlacementType;
  key: string | number;
  multiple: boolean;
  style?: Record<string, string>;
}

interface Props<T> extends AvatarTooltipsProps {
  className?: string;
  items: Array<T>;
  renderItemAvatar: (
    item: T,
    props: SingleAvatarAdditionalProps
  ) => JSX.Element;
  renderItemTooltipText: (item: T) => ReactNode;
  serializeItem: (item: T) => string;
  getItemId: (item: T) => string | number;
  tooltipListLimit?: number;
  circle?: boolean;
}

function BaseMultipleBaseAvatar<T>({
  items,
  getItemId,
  renderItemTooltipText,
  tooltipTitle,
  tooltipListLimit = 10,
  renderItemAvatar,
  className,
  tooltipPlacement,
  circle,
}: Props<T>): JSX.Element {
  function itemsTooltip() {
    return (
      <div className="text-left">
        {getTooltipContent(
          <TooltipList
            title={null}
            items={items.map((item) => ({
              id: getItemId(item),
              name: renderItemTooltipText(item),
            }))}
            limit={tooltipListLimit}
          />,
          typeof tooltipTitle !== 'function' ? (
            <div className="mb-2">{tooltipTitle}</div>
          ) : (
            tooltipTitle
          )
        )}
      </div>
    );
  }
  const getIEFlexSize = (numItems: number) => ({
    msFlexPreferredSize: `${100 / numItems}%`,
  });

  function renderItem(
    item: T,
    additionalProps: {
      key: string | number;
      multiple: boolean;
      style?: Record<string, string>;
    }
  ) {
    return renderItemAvatar(item, {
      className,
      tooltipTitle,
      tooltipPlacement,
      ...additionalProps,
    });
  }

  if (items.length === 1) {
    return renderItem(items[0], {
      key: 0,
      multiple: false,
    });
  }

  const grid = getBalancedGrid(
    Math.min(items.length, MAX_USERS_SHOW),
    MAX_COLS
  );
  const lineStyle = getIEFlexSize(grid.length);

  return (
    <AttachTooltip placement={tooltipPlacement} tooltipContent={itemsTooltip()}>
      {(handlers) => {
        let itemIndex = 0;
        return (
          <StyledBoxDIV
            className={cs(className, 'border-avatar', {
              circle,
            })}
            {...handlers}
          >
            {grid.map((cols, line) => {
              const colStyle = getIEFlexSize(cols);
              return (
                <div
                  className={`line cols-${cols}`}
                  key={line}
                  // @ts-expect-error TS complains about IE specific CSS rule
                  style={lineStyle}
                >
                  {range(cols).map(() => {
                    const item = items[itemIndex++];
                    return renderItem(item, {
                      key: getItemId(item),
                      multiple: true,
                      style: colStyle,
                    });
                  })}
                </div>
              );
            })}
            <div
              className={`counter ${
                items.length > 99 ? 'hundred' : items.length > 9 ? 'dozen' : ''
              }`}
            >
              {items.length}
            </div>
          </StyledBoxDIV>
        );
      }}
    </AttachTooltip>
  );
}

export const MultipleBaseAvatar = React.memo(
  BaseMultipleBaseAvatar,
  (
    {
      className,
      items,
      serializeItem,
      tooltipPlacement,
      tooltipTitle,
      tooltipListLimit,
    },
    nextProps
  ) =>
    className == nextProps.className &&
    tooltipPlacement == nextProps.tooltipPlacement &&
    tooltipTitle == nextProps.tooltipTitle &&
    tooltipListLimit == nextProps.tooltipListLimit &&
    items.length === items.length &&
    items.map(serializeItem).join('') ==
      nextProps.items.map(serializeItem).join('')
) as typeof BaseMultipleBaseAvatar;

const StyledBoxDIV = styled<any>(SingleStyledBoxDIV)`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  &.circle {
    border-radius: 50%;
  }

  &.border-avatar {
    border: 2px solid #fff;
    box-shadow: 0 0 3px 0px rgba(0, 0, 0, 0.25);

    > .name {
      line-height: inherit;
    }
  }

  > .counter {
    position: absolute;
    left: 25%;
    top: 25%;
    bottom: 25%;
    right: 25%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 100%;
    font-size: 1rem;
    text-shadow: 0px 0px 1px black;

    &.dozen {
      left: 10%;
      top: 10%;
      bottom: 10%;
      right: 10%;
    }

    &.hundred {
      left: 5%;
      top: 5%;
      bottom: 5%;
      right: 5%;
      font-size: 0.8rem;
    }
  }

  > .line {
    flex-basis: 100%;
    display: flex;
    flex-wrap: nowrap;

    &.cols-2 .name {
      font-size: 0.8rem;
    }

    &.cols-3 .name {
      font-size: 0.6rem;
    }
  }
`;
